import { App, Button, Col, Form, Input, Radio, Row, Select, Typography } from 'antd'
import type { OrderGroup } from '@super-mega/server/contracts/order'
import { Authorized } from '@/features/authorization/authorized.tsx'
import { OrderGroupSelectionField } from './order-group-selection-field.tsx'
import type { NumberOption, OrderGroupMode, ServiceOrderCreateFormValues } from './service-order-form.types.ts'

export function ServiceOrderCreateOwnershipFields({
  customerId,
  customerLoading,
  customerOptions,
  disabled,
  customerLocked = false,
  hasEnterpriseSelection = false,
  description = '以下信息只填写一次，并应用到本次创建的全部服务订单。',
  orderGroupHint = '订单组不是必填项；不加入时，每个服务订单独立存在。',
  onCreateCustomer,
  onCreateSalesperson,
  onCustomerChange,
  onCustomerSearch,
  onClearOrderGroup,
  onOpenOrderGroupSelector,
  onOrderGroupModeChange,
  onSalespersonChange,
  onSalespersonSearch,
  organizationLoading = false,
  organizationOptions,
  orderGroup,
  salespersonLoading,
  salespersonOptions
}: {
  customerId?: number
  customerLoading: boolean
  customerOptions: NumberOption[]
  disabled: boolean
  customerLocked?: boolean
  hasEnterpriseSelection?: boolean
  description?: string
  orderGroupHint?: string
  onCreateCustomer?: () => void
  onCreateSalesperson?: () => void
  onCustomerChange: (value: number) => void
  onCustomerSearch: (value: string) => void
  onClearOrderGroup: () => void
  onOpenOrderGroupSelector: () => void
  onOrderGroupModeChange: (mode: OrderGroupMode) => void
  onSalespersonChange?: (value: number) => void
  onSalespersonSearch: (value: string) => void
  organizationLoading?: boolean
  organizationOptions?: NumberOption[]
  orderGroup?: OrderGroup
  salespersonLoading: boolean
  salespersonOptions: NumberOption[]
}) {
  const form = Form.useFormInstance<ServiceOrderCreateFormValues>()
  const orderGroupMode = Form.useWatch('orderGroupMode', form) ?? 'none'
  const showOrderGroupName = orderGroupMode === 'new'
  const showExistingOrderGroup = orderGroupMode === 'existing'
  const hasOrganization = organizationOptions !== undefined

  const selectOrderGroup = () => {
    if (customerId) {
      onOpenOrderGroupSelector()
    }
  }

  return (
    <section className="border-border bg-surface scroll-mt-24 rounded-xl border p-5 shadow-xs" id="section-order-info">
      <div className="mb-5">
        <Typography.Title className="mb-1!" level={5}>
          订单归属
        </Typography.Title>
        <Typography.Text type="secondary">{description}</Typography.Text>
      </div>
      <Row gutter={[24, 0]}>
        {hasOrganization ? (
          <Col md={8} xs={24}>
            <Form.Item label="经营主体" name="organizationId" rules={[{ message: '请选择经营主体', required: true }]}>
              <Select
                disabled={disabled}
                loading={organizationLoading}
                options={organizationOptions}
                placeholder="请选择经营主体"
                showSearch={{ optionFilterProp: 'label' }}
              />
            </Form.Item>
          </Col>
        ) : null}
        <Col md={hasOrganization ? 8 : 12} xs={24}>
          <Form.Item label="客户" htmlFor="customerId" required>
            <div className="flex gap-1.5">
              <Form.Item name="customerId" noStyle rules={[{ message: '请选择客户', required: true }]}>
                <CustomerSelect
                  style={{ flex: 1, minWidth: 0 }}
                  disabled={disabled || customerLocked}
                  confirmChange={hasEnterpriseSelection}
                  loading={customerLoading}
                  onChange={onCustomerChange}
                  options={customerOptions}
                  placeholder="请选择客户"
                  showSearch={{ onSearch: onCustomerSearch, filterOption: false }}
                />
              </Form.Item>
              {onCreateCustomer ? (
                <Authorized permissions={['order.service.create']}>
                  <Button variant={'outlined'} disabled={disabled || customerLocked} onClick={onCreateCustomer}>
                    快速创建
                  </Button>
                </Authorized>
              ) : null}
            </div>
          </Form.Item>
        </Col>
        <Col md={hasOrganization ? 8 : 12} xs={24}>
          <Form.Item label="业务员（可选）" htmlFor="salespersonId">
            <div className="flex gap-1.5">
              <Form.Item name="salespersonId" noStyle>
                <Select
                  style={{ flex: 1, minWidth: 0 }}
                  disabled={disabled}
                  loading={salespersonLoading}
                  onChange={onSalespersonChange}
                  options={salespersonOptions}
                  allowClear
                  placeholder="可不选择业务员"
                  showSearch={{ onSearch: onSalespersonSearch, filterOption: false }}
                />
              </Form.Item>
              {onCreateSalesperson ? (
                <Authorized permissions={['order.service.create']}>
                  <Button variant={'outlined'} disabled={disabled} onClick={onCreateSalesperson}>
                    快速创建
                  </Button>
                </Authorized>
              ) : null}
            </div>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={[24, 0]}>
        <Col md={12} xs={24}>
          <Form.Item extra={orderGroupHint} label="订单组（可选）" name="orderGroupMode">
            <Radio.Group
              block
              disabled={disabled}
              onChange={(event) => onOrderGroupModeChange(event.target.value as OrderGroupMode)}
              optionType="button"
              options={[
                { label: '不加入', value: 'none' },
                { label: '新建', value: 'new' },
                { label: '选择已有', value: 'existing' }
              ]}
            />
          </Form.Item>
        </Col>
        {showOrderGroupName ? (
          <Col md={12} xs={24}>
            <Form.Item extra="不填写时，系统会自动生成订单组号。" label="订单组名称（可选）" name="orderGroupName">
              <Input disabled={disabled} maxLength={255} placeholder="可填写名称，也可以留空自动生成" />
            </Form.Item>
          </Col>
        ) : null}
        {showExistingOrderGroup ? (
          <Col md={12} xs={24}>
            <OrderGroupSelectionField
              customerId={customerId}
              disabled={disabled}
              orderGroup={orderGroup}
              onSelect={selectOrderGroup}
              onClear={onClearOrderGroup}
            />
          </Col>
        ) : null}
      </Row>
    </section>
  )
}

function CustomerSelect({
  value,
  onChange,
  confirmChange,
  ...props
}: React.ComponentProps<typeof Select<number>> & { confirmChange: boolean }) {
  const { modal } = App.useApp()
  return (
    <Select<number>
      {...props}
      value={value}
      onChange={(next, option) => {
        if (next === value) {
          return
        }
        if (!confirmChange) {
          onChange?.(next, option)
          return
        }
        modal.confirm({
          title: '切换客户并清空服务对象资料？',
          content: '已选择的服务对象、待提交企业资料和订单组将清空，其他订单内容保留。',
          okText: '切换并清空',
          cancelText: '保留当前客户',
          onOk: () => onChange?.(next, option)
        })
      }}
    />
  )
}
